import { useEffect, useId, useRef, type ReactNode } from 'react';
import { useDialogFocus } from '../../lib/dialogFocus';
import { X } from 'lucide-react';
import { Button } from './button';
import { cn } from '../../lib/utils';

/**
 * The one modal shell (digitaladmin's EditorModal role). Deliberately not a
 * portal library yet — Escape, backdrop click and focus trap arrive with the
 * Radix dialog when the field builder needs nested layering.
 *
 * The panel is HEIGHT-CAPPED and its body scrolls. Kai, 2026-09-08, on the
 * new-ticket modal: *"the form isn't scrollable and the save button is off
 * the bottom."* It had no `max-h` at all, so a form taller than the viewport
 * simply ran off the screen taking its Save button with it — and because the
 * page behind is `h-screen overflow-hidden`, there was nothing to scroll
 * either. Fixed here rather than in that one form: every modal grows past the
 * fold eventually, and `Sheet` has had exactly this three-line treatment
 * since it was written.
 */
export function Modal({
  open,
  onClose,
  title,
  children,
  wide,
  footer,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  wide?: boolean;
  /** Pinned below the scroll area. A form's Save belongs here, not at the
   *  bottom of the content — Kai found the new-ticket modal's *"save button
   *  off the bottom"*, and merely making the body scroll would have left it
   *  reachable-but-hidden, which is the same complaint one scroll later. */
  footer?: ReactNode;
}) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);
  // Step 140 — focus moves in on open, stays in while open (Tab wraps), and
  // goes back to the trigger on close; the page behind stops scrolling.
  const panelRef = useRef<HTMLDivElement>(null);
  const titleId = useId();
  useDialogFocus(open, panelRef);
  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-black/50 p-4 pt-[8vh]"
      onClick={onClose}
    >
      <div
        className={cn(
          'flex w-full flex-col rounded-lg border bg-card shadow-lg',
          // 8vh of top offset plus the wrapper's own padding — leave the rest.
          'max-h-[84vh]',
          wide ? 'max-w-2xl' : 'max-w-lg',
        )}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        ref={panelRef}
        tabIndex={-1}
      >
        {/* Header and body are separate flex children so the title stays put
            while the content scrolls under it; `shrink-0` because a long
            title would otherwise squeeze it instead of wrapping. */}
        <div className="flex shrink-0 items-center justify-between border-b px-4 py-3">
          <h2 id={titleId} className="text-sm font-semibold">
            {title}
          </h2>
          <Button variant="ghost" size="icon" onClick={onClose} aria-label="Close">
            <X className="h-4 w-4" />
          </Button>
        </div>
        {/* min-h-0 is what makes flex-1 mean anything here — without it the
            body refuses to shrink below its content and the cap does nothing.
            The same lesson AppShell's <main> carries. */}
        <div className="min-h-0 flex-1 overflow-y-auto p-4">{children}</div>
        {footer && <div className="shrink-0 border-t px-4 py-3">{footer}</div>}
      </div>
    </div>
  );
}
